import { PlusIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { ApiResponse } from "@/api/rest/core/types";
import { ChatPresetQuestionsConfiguration } from "@/api/rest/models/configurations";
import {
  EntityConfigurationEntityType,
  EntityConfigurationType,
  useUpdateConfiguration,
} from "@/api/rest/services/configurationService";
import { ApiBanner } from "@/components/common/Banner";
import Button from "@/components/common/Button";
import Checkbox from "@/components/common/Checkbox";
import Field from "@/components/common/Field";
import Input from "@/components/common/Input";

export interface ChatPresetEntry {
  language: string;
  questions: string[];
}

interface ChatPresetQuestionsSettingsProps {
  initialConfiguration: ChatPresetQuestionsConfiguration;
  buildingId: number;
}

export function ChatPresetQuestionsSettings({ initialConfiguration, buildingId }: ChatPresetQuestionsSettingsProps) {
  const { t } = useTranslation();
  const { mutate: updateConfiguration, isPending: isSaving } = useUpdateConfiguration();

  const [isEnabled, setIsEnabled] = useState(initialConfiguration.enabled);
  const [presets, setPresets] = useState<ChatPresetEntry[]>(initialConfiguration.presets);

  const [saveResult, setSaveResult] = useState<ApiResponse<unknown> | null>(null);

  useEffect(() => {
    setIsEnabled(initialConfiguration.enabled);
    setPresets(initialConfiguration.presets);
  }, [initialConfiguration]);

  const handleAddPreset = () => {
    setPresets((prev) => [...prev, { language: "", questions: ["", "", ""] }]);
    setSaveResult(null);
  };

  const handleRemovePreset = (index: number) => {
    const newPresets = presets.filter((_, i) => i !== index);
    setPresets(newPresets);
    if (newPresets.length === 0) {
      setIsEnabled(false);
      handleSave(false, { showBanner: false });
    }
  };

  const handleLanguageChange = (index: number, value: string) => {
    const newPresets = [...presets];
    newPresets[index] = { ...newPresets[index], language: value };
    setPresets(newPresets);
    setSaveResult(null);
  };

  const handleQuestionChange = (presetIndex: number, questionIndex: number, value: string) => {
    const newPresets = [...presets];
    const newQuestions = [...newPresets[presetIndex].questions];
    newQuestions[questionIndex] = value;
    newPresets[presetIndex] = { ...newPresets[presetIndex], questions: newQuestions };
    setPresets(newPresets);
    setSaveResult(null);
  };

  const validatePresets = () => {
    for (const preset of presets) {
      if (preset.language && !/^[a-zA-Z]{2}$/.test(preset.language)) {
        setSaveResult({
          success: false,
          message: t("chatPresetSettings.errors.invalidLanguage"),
          result: undefined,
        });
        return false;
      }

      const hasAtLeastOneQuestion = preset.questions.some((q) => q.trim() !== "");
      if (!hasAtLeastOneQuestion) {
        setSaveResult({
          success: false,
          message: t("chatPresetSettings.errors.noQuestions"),
          result: undefined,
        });
        return false;
      }
    }
    return true;
  };

  const handleSave = (enabled?: boolean, options?: { showBanner: boolean }) => {
    if (options?.showBanner && !validatePresets()) {
      return;
    }
    if (options?.showBanner) {
      setSaveResult(null);
    }

    const cleanedPresets = presets.map((preset) => ({
      ...preset,
      questions: preset.questions.filter((q) => q.trim() !== ""),
    }));

    const configToSave: ChatPresetQuestionsConfiguration = {
      enabled: enabled ?? isEnabled,
      presets: cleanedPresets,
    };

    updateConfiguration(
      {
        entityType: EntityConfigurationEntityType.BUILDING,
        buildingId: buildingId,
        configurationType: EntityConfigurationType.CHAT_PRESET_QUESTIONS,
        configurationValue: JSON.stringify(configToSave),
      },
      {
        onSuccess: () => {
          if (options?.showBanner) {
            setSaveResult({ success: true, result: {} });
          }
          initialConfiguration.enabled = enabled ?? isEnabled;
        },
        onError: (error) => {
          if (options?.showBanner) {
            setSaveResult({
              success: false,
              message: error.message,
              result: undefined,
            });
          }
        },
      },
    );
  };

  return (
    <div>
      <Checkbox
        label={t("chatPresetSettings.enable")}
        id="chat-presets-enabled"
        checked={isEnabled}
        onChange={(e) => {
          const isChecked = e.target.checked;
          setIsEnabled(isChecked);
          if (isChecked && presets.length === 0) {
            handleAddPreset();
          }
          handleSave(isChecked, { showBanner: false });
        }}
      />

      {isEnabled && (
        <div className="mt-4 pl-2">
          {presets.map((preset, presetIndex) => (
            <div key={presetIndex} className="relative mb-4 p-3 border rounded-md bg-slate-50">
              <button
                onClick={() => handleRemovePreset(presetIndex)}
                className="absolute top-1 right-1 p-1 text-red-600 hover:text-red-800"
                aria-label={t("chatPresetSettings.removePreset")}
              >
                <XMarkIcon className="h-5 w-5" />
              </button>

              <Field id={`preset-lang-${presetIndex}`} className="mb-3">
                <Field.Label>{t("chatPresetSettings.language")}</Field.Label>
                <Input
                  type="text"
                  value={preset.language}
                  onChange={(e) => handleLanguageChange(presetIndex, e.target.value)}
                  className="max-w-xs"
                />
              </Field>

              <div className="space-y-2">
                <Field.Label>{t("chatPresetSettings.questions")}</Field.Label>
                {preset.questions.map((question, questionIndex) => (
                  <Field id={`preset-${presetIndex}-question-${questionIndex}`} key={questionIndex}>
                    <Input
                      type="text"
                      value={question}
                      onChange={(e) => handleQuestionChange(presetIndex, questionIndex, e.target.value)}
                      placeholder={t("chatPresetSettings.questionPlaceholder")}
                    />
                  </Field>
                ))}
              </div>
            </div>
          ))}

          <div className="flex items-center justify-between mt-4 w-full gap-4">
            <div className="flex items-center gap-4">
              <Button variant="outline" onClick={handleAddPreset} className="flex items-center">
                <PlusIcon className="h-5 w-5 mr-2" />
                {t("chatPresetSettings.addPreset")}
              </Button>
              <Button
                onClick={() => handleSave(undefined, { showBanner: true })}
                disabled={isSaving}
                loading={isSaving}
              >
                {isSaving ? t("settings.saving") : t("settings.save")}
              </Button>
            </div>
            <div className="flex-grow">
              {saveResult && (
                <ApiBanner apiResponse={saveResult} successMessage={t("chatPresetSettings.success.saved")} />
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
